/**
 * @license
 * @copyright Copyright Motili Inc., 2021 All Rights Reserved
 */

import { ChangeEvent, ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useAppDispatch } from '../../../common/redux/hooks';
import { setCompanyInfo } from '../../../common/redux/ducks/contractorRegistrationForm';
import { CompanyInfo } from '../../../common/types';
import StyledTextInput from '../../../components/styled/StyledTextInput';
import StateDropdown from './StateDropdown';

interface AddressFormProps {
    companyInfo: CompanyInfo;
    addressType: 'billingAddress' | 'companyAddress';
}

export default function AddressForm({
    companyInfo,
    addressType,
}: AddressFormProps): ReactElement {
    const dispatch = useAppDispatch();
    const {
        register,
        formState: { errors },
    } = useForm({
        mode: 'all',
        defaultValues: {
            address1: companyInfo[addressType]?.address1,
            address2: companyInfo[addressType]?.address2,
            city: companyInfo[addressType]?.city,
            zip: companyInfo[addressType]?.zip,
        },
    });
    const setCompanyAndBillingAddress = (
        target: EventTarget & HTMLInputElement
    ) => {
        dispatch(
            setCompanyInfo({
                ...companyInfo,
                companyAddress: {
                    ...companyInfo.companyAddress,
                    [target.name]: target.value,
                },
                billingAddress: {
                    ...companyInfo.billingAddress,
                    [target.name]: target.value,
                },
            })
        );
    };

    const handleInputChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
        if (companyInfo.useCompanyAddressAsBilling) {
            setCompanyAndBillingAddress(target);
        } else {
            dispatch(
                setCompanyInfo({
                    ...companyInfo,
                    [addressType]: {
                        ...companyInfo[addressType],
                        [target.name]: target.value,
                    },
                })
            );
        }
    };

    return (
        <div>
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                required
                name={'address1'}
                error={!!errors.address1}
                errorMessage={(errors?.address1?.message as string) || ''}
                register={register('address1', {
                    required: 'Address cannot be blank',
                })}
                onChange={handleInputChange}
                value={companyInfo[addressType].address1}
                style={inputStyles}
                label={
                    addressType === 'billingAddress'
                        ? 'Billing Address'
                        : 'Company Address'
                }
                placeholder='Street Address'
            />
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                name={'address2'}
                register={register('address2', {
                    required: false,
                })}
                onChange={handleInputChange}
                value={companyInfo[addressType].address2}
                style={inputStyles}
                label=''
                placeholder='Address Line 2'
            />
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                name={'city'}
                register={register('city', {
                    required: 'City cannot be blank',
                })}
                error={!!errors?.city}
                errorMessage={(errors?.city?.message as string) || ''}
                onChange={handleInputChange}
                value={companyInfo[addressType].city}
                style={inputStyles}
                label=''
                placeholder='City'
            />
            <div style={{ marginTop: '20px' }}>
                <StateDropdown
                    companyInfo={companyInfo}
                    addressType={addressType}
                />
            </div>
            <StyledTextInput
                marginTop='20px'
                withLabelSpacing
                name={'zip'}
                register={register('zip', {
                    required: 'Zip code cannot be blank',
                    pattern: {
                        value: /(^\d{5}$)/,
                        message: 'Zip code must be a 5 digit number',
                    },
                })}
                error={!!errors?.zip}
                errorMessage={(errors?.zip?.message as string) || ''}
                onChange={handleInputChange}
                value={companyInfo[addressType].zip}
                style={inputStyles}
                label=''
                maxLength={5}
                placeholder='Zip Code'
            />
        </div>
    );
}

const inputStyles = { width: '100%' };
